import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams, HttpHeaders } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { ApiResponse } from '../models';
import { environment } from '../../../environments/environment';

@Injectable({ providedIn: 'root' })
export class ApiService {
  private http = inject(HttpClient);
  private readonly apiBaseUrl = environment.baseUrl + '/api/v1/admin';

  private headers(includeContentType = true): HttpHeaders {
    const h: Record<string, string> = {};
    if (includeContentType) h['Content-Type'] = 'application/json';
    return new HttpHeaders(h);
    // Let authInterceptor handle Authorization
  }

  // No Content-Type header at all — browser sets multipart/form-data + boundary automatically
  private formHeaders(): HttpHeaders {
    return new HttpHeaders();
  }

  private buildParams(params?: Record<string, any>): HttpParams {
    let p = new HttpParams();
    if (params) {
      Object.entries(params).forEach(([k, v]) => {
        if (v !== null && v !== undefined) p = p.set(k, v);
      });
    }
    return p;
  }

  private handleError(error: any): Observable<never> {
    return throwError(() => error);
  }

  // ── JSON methods ────────────────────────────────────────────────────────────

  get<T>(endpoint: string, params?: Record<string, any>): Observable<T> {
    return this.http
      .get<ApiResponse<T>>(`${this.apiBaseUrl}${endpoint}`, {
        headers: this.headers(false),
        params:  this.buildParams(params),
      })
      .pipe(map(res => res.data), catchError(e => this.handleError(e)));
  }

  post<T>(endpoint: string, body: any): Observable<T> {
    return this.http
      .post<ApiResponse<T>>(`${this.apiBaseUrl}${endpoint}`, body, {
        headers: this.headers(),
      })
      .pipe(map(res => res.data), catchError(e => this.handleError(e)));
  }

  put<T>(endpoint: string, body: any): Observable<T> {
    return this.http
      .put<ApiResponse<T>>(`${this.apiBaseUrl}${endpoint}`, body, {
        headers: this.headers(),
      })
      .pipe(map(res => res.data), catchError(e => this.handleError(e)));
  }

  patch<T>(endpoint: string, body: any): Observable<T> {
    return this.http
      .patch<ApiResponse<T>>(`${this.apiBaseUrl}${endpoint}`, body, {
        headers: this.headers(),
      })
      .pipe(map(res => res.data), catchError(e => this.handleError(e)));
  }

  delete<T>(endpoint: string): Observable<T> {
    return this.http
      .delete<ApiResponse<T>>(`${this.apiBaseUrl}${endpoint}`, {
        headers: this.headers(false),
      })
      .pipe(map(res => res.data), catchError(e => this.handleError(e)));
  }

  // ── FormData methods (multipart/form-data) ──────────────────────────────────
  // Do NOT set Content-Type — the browser sets it automatically with the correct
  // boundary string (e.g. multipart/form-data; boundary=------WebKitFormBoundary...)
  // Setting it manually strips the boundary and breaks multipart parsing on the server.

  postForm<T>(endpoint: string, body: FormData): Observable<T> {
    return this.http
      .post<ApiResponse<T>>(`${this.apiBaseUrl}${endpoint}`, body, {
        headers: this.formHeaders(),
      })
      .pipe(map(res => res.data), catchError(e => this.handleError(e)));
  }

  putForm<T>(endpoint: string, body: FormData): Observable<T> {
    return this.http
      .put<ApiResponse<T>>(`${this.apiBaseUrl}${endpoint}`, body, {
        headers: this.formHeaders(),
      })
      .pipe(map(res => res.data), catchError(e => this.handleError(e)));
  }
}